import React from 'react';

import type { StyleRun } from '@rta/rta-blueprint-library';

import { type EffectiveTextStyle, toEffectiveStyle } from './read-style-runs';
import { normalizeStyleRuns } from './apply-style-run';

/** One contiguous slice of annotation text plus the run overrides on it (null = inherit the box-level scalars) */
export type StyledTextSegment = {
	text: string;
	style: EffectiveTextStyle | null;
};

/** Splits text into styled/unstyled slices in document order -- the data behind both the read-mode renderer and the edit-mode mirror/measurement */
export const toStyledTextSegments = (text: string, runs: Array<StyleRun> | undefined): Array<StyledTextSegment> => {
	if (!runs || runs.length === 0) return text ? [{ text, style: null }] : [];

	const segments: Array<StyledTextSegment> = [];
	let cursor = 0;
	normalizeStyleRuns(runs).forEach((run) => {
		const start = Math.max(cursor, Math.min(run.start, text.length));
		const end = Math.max(start, Math.min(run.end, text.length));
		if (start > cursor) segments.push({ text: text.slice(cursor, start), style: null });
		if (end > start) segments.push({ text: text.slice(start, end), style: toEffectiveStyle(run) });
		cursor = Math.max(cursor, end);
	});
	if (cursor < text.length) segments.push({ text: text.slice(cursor), style: null });
	return segments;
};

export type RenderAnnotationTextRunsOptions = {
	/** Maps a run's fontSize into the surface's own units -- SKU overhead normalizes and scales, blueprint pages use raw px */
	resolveFontSize?: (fontSize: number) => number;
};

/** The inline CSS for one segment; inline-only so it survives html2canvas serializing the SVG to a data URL */
export const toStyleRunCss = (style: EffectiveTextStyle, options: RenderAnnotationTextRunsOptions = {}): React.CSSProperties => {
	const { resolveFontSize } = options;
	const { color, backgroundColor, fontWeight, fontStyle, textDecoration, fontSize, fontFamily } = style;

	return {
		...(color ? { color } : {}),
		...(backgroundColor ? { backgroundColor } : {}),
		...(fontWeight ? { fontWeight } : {}),
		...(fontStyle ? { fontStyle } : {}),
		...(textDecoration ? { textDecoration } : {}),
		...(typeof fontSize === 'number' ? { fontSize: `${resolveFontSize ? resolveFontSize(fontSize) : fontSize}px` } : {}),
		...(fontFamily ? { fontFamily: `${fontFamily}, sans-serif` } : {})
	};
};

/**
 * Renders textbox text as plain text plus inline-styled `<span>` segments for any style runs (#390 --
 * per-selection formatting layered on top of the whole-box scalars). Both surfaces render annotation text as
 * HTML inside a `<foreignObject>`, so this one emitter serves the SKU overhead, the blueprint pages and the
 * edit-mode mirror div.
 */
export const renderAnnotationTextRuns = (text: string, runs: Array<StyleRun> | undefined, options: RenderAnnotationTextRunsOptions = {}): React.ReactNode => {
	if (!runs || runs.length === 0) return text;

	return toStyledTextSegments(text, runs).map((segment, index) => (segment.style
		? <span key={`run-${index}`} style={toStyleRunCss(segment.style, options)}>{segment.text}</span>
		: <React.Fragment key={`plain-${index}`}>{segment.text}</React.Fragment>));
};
